import type { SxProps, Theme } from '@mui/material/styles';
import { NAV_COLOR_TOKENS } from './constants';

type NavStyleOptions = {
  isCollapsed: boolean;
  isActive: boolean;
  isMobileDialog: boolean;
  isDisabled: boolean;
};

export const navContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  flex: 1,
  width: '100%',
};

export const navGroupSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  width: '100%',
  '.nav-label': {
    marginLeft: '0px!important',
  },
};

export const navItemSx =
  ({
    isCollapsed,
    isActive,
    isMobileDialog,
    isDisabled,
  }: NavStyleOptions): SxProps<Theme> =>
  (theme) => {
    const tokens: (typeof NAV_COLOR_TOKENS)[keyof typeof NAV_COLOR_TOKENS] =
      isMobileDialog ? NAV_COLOR_TOKENS.mobile : NAV_COLOR_TOKENS.desktop;

    return {
      display: 'flex',
      flexDirection: isCollapsed ? 'column' : 'row',
      alignItems: isCollapsed ? 'center' : 'center',
      justifyContent: isCollapsed ? 'center' : 'flex-start',
      width: '100%',
      px: isCollapsed ? 1 : '4px',
      py: 1.5,
      gap: isCollapsed ? 0.5 : 1.5,
      textDecoration: 'none',
      textAlign: isCollapsed ? 'center' : 'left',
      overflow: 'visible',
      transition: theme.transitions.create(['background-color', 'color'], {
        duration: theme.transitions.duration.short,
      }),
      color: isActive ? tokens.activeText : tokens.text,
      ...(isDisabled
        ? { opacity: 0.5, cursor: 'not-allowed' }
        : { cursor: 'pointer' }),
      '& .nav-label': {
        marginLeft: '0px!important',
        textWrap: 'wrap',
      },
      '&:hover': {
        color: isDisabled
          ? isActive
            ? tokens.activeText
            : tokens.text
          : tokens.hoverText ?? tokens.text,
        '& .nav-icon-wrap': {
          backgroundColor: isDisabled
            ? isActive
              ? tokens.activeBg
              : 'transparent'
            : tokens.hoverBg,
        },
        '& .nav-icon': {
          color: isDisabled
            ? isActive
              ? tokens.activeIcon
              : tokens.icon
            : tokens.hoverIcon ?? tokens.icon,
        },
      },
    };
  };

export const navIconSx = ({
  isActive,
  isMobileDialog,
}: Pick<NavStyleOptions, 'isActive' | 'isMobileDialog'>): SxProps<Theme> => {
  const tokens = isMobileDialog
    ? NAV_COLOR_TOKENS.mobile
    : NAV_COLOR_TOKENS.desktop;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    color: isActive ? tokens.activeIcon : tokens.icon,
  };
};

export const navIconWrapSx = ({
  isActive,
  isMobileDialog,
}: Pick<NavStyleOptions, 'isActive' | 'isMobileDialog'>): SxProps<Theme> => {
  const tokens = isMobileDialog
    ? NAV_COLOR_TOKENS.mobile
    : NAV_COLOR_TOKENS.desktop;

  return {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isActive ? tokens.activeBg : 'transparent',
    transition: 'background-color 150ms ease',
    overflow: 'visible',
  };
};

export const navLabelSx =
  ({ isActive }: Pick<NavStyleOptions, 'isActive'>) =>
  (theme: Theme) => ({
    marginLeft: 0,
    fontSize: '11px',
    letterSpacing: '0.15px',
    fontWeight: isActive
      ? theme.typography.fontWeightBold ?? 700
      : theme.typography.fontWeightMedium ?? 500,
    lineHeight: '16px',
    opacity: 0,
    animation: 'navLabelFadeIn 200ms ease 180ms forwards',
    '@keyframes navLabelFadeIn': {
      from: { opacity: 0, transform: 'translateX(-2px)' },
      to: { opacity: 1, transform: 'translateX(0)' },
    },
  });

export const navLabelCollapsedSx =
  ({ isActive }: Pick<NavStyleOptions, 'isActive'>) =>
  (theme: Theme) => ({
    marginLeft: 0,
    fontSize: '9px',
    letterSpacing: '0.15px',
    fontWeight: isActive
      ? theme.typography.fontWeightBold ?? 700
      : theme.typography.fontWeightMedium ?? 500,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    lineHeight: '12px',
    overflow: 'visible',
    display: 'block',
    alignSelf: 'center',
    width: 'calc(100% + 16px)',
  });

export const navBadgeSx: SxProps<Theme> = {
  marginLeft: 'auto',
  fontSize: '0.75rem',
  color: 'text.secondary',
};
